import React, { useState } from 'react'

import FeatureNotEnabled from '../../components/FeatureNotEnabled'
import { Card } from '../../components/UI'
import {
    Fingerprint, Eye, EyeOff, Shield, Activity,
    Settings2, Info, Hash, Code2, CheckCircle2
} from 'lucide-react'

type TabType = 'overview' | 'demo' | 'config' | 'docs'

export default function WatermarkPage() {
    const [tab, setTab] = useState<TabType>('overview')
    const [wmVisible, setWmVisible] = useState(false)

    const techniques = [
        { name: 'DOM Watermark', desc: 'Elemento invisible con ID de licencia inyectado en el DOM', status: 'active', layer: 'HTML' },
        { name: 'CSS ::after', desc: 'Pseudo-elemento con contenido del client_id en opacity: 0', status: 'active', layer: 'CSS' },
        { name: 'Canvas Pixel', desc: 'Píxeles de marca de agua en imágenes generadas por Canvas', status: 'planned', layer: 'Canvas' },
        { name: 'Audio Fingerprint', desc: 'Señal inaudible embebida en audio generado', status: 'planned', layer: 'Audio' },
        { name: 'Console Watermark', desc: 'Console.log con ASCII art + license ID al abrir DevTools', status: 'active', layer: 'JS' },
        { name: 'Meta Tag', desc: 'Meta tag personalizado con hash de licencia', status: 'active', layer: 'HTML' },
    ]

    const tabs = [
        { id: 'overview' as const, label: 'Técnicas', icon: <Activity size={16} /> },
        { id: 'demo' as const, label: 'Demo', icon: <Eye size={16} /> },
        { id: 'config' as const, label: 'Config', icon: <Settings2 size={16} /> },
        { id: 'docs' as const, label: 'Docs', icon: <Info size={16} /> },
    ]

    return (
        <div className="p-6 max-w-5xl mx-auto pb-20">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-white mb-1 flex items-center gap-3">
                    <Fingerprint className="text-neon-blue" /> Watermarking Invisible
                </h1>
                <p className="text-gray-500 text-sm">Técnicas invisibles de copy-protection</p>
            </div>

            <FeatureNotEnabled moduleName="Watermarking" />

            <Card className="mb-6 bg-neon-blue/5 border border-neon-blue/20">
                <div className="flex items-center gap-4">
                    <Shield size={24} className="text-neon-blue" />
                    <div>
                        <span className="text-neon-blue font-bold text-sm">4 técnicas activas · 2 planificadas</span>
                        <p className="text-gray-400 text-xs mt-0.5">Cada copia contiene marcadores únicos vinculados al license_id</p>
                    </div>
                </div>
            </Card>

            <div className="flex gap-1 mb-6 bg-charcoal rounded-lg p-1 border border-white/5">
                {tabs.map(t => (
                    <button key={t.id} onClick={() => setTab(t.id)} className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${tab === t.id ? 'bg-neon-blue/10 text-neon-blue border border-neon-blue/20' : 'text-gray-500 hover:text-white'}`}>
                        {t.icon} {t.label}
                    </button>
                ))}
            </div>

            {tab === 'overview' && (
                <div className="space-y-2">
                    {techniques.map(t => (
                        <Card key={t.name} className="!p-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <Hash size={16} className={t.status === 'active' ? 'text-neon-green' : 'text-gray-600'} />
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-white font-medium text-sm">{t.name}</span>
                                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono ${t.status === 'active' ? 'bg-neon-green/10 text-neon-green' : 'bg-yellow-500/10 text-yellow-400'}`}>{t.status}</span>
                                            <span className="text-[10px] text-gray-600 bg-white/5 px-1.5 py-0.5 rounded">{t.layer}</span>
                                        </div>
                                        <p className="text-gray-500 text-xs mt-0.5">{t.desc}</p>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            )}

            {tab === 'demo' && (
                <Card>
                    <h2 className="text-white font-bold text-sm mb-4 flex items-center gap-2"><Eye size={16} className="text-neon-blue" /> Visualizar Watermarks</h2>
                    <button onClick={() => setWmVisible(!wmVisible)} className="px-4 py-2.5 bg-neon-blue/10 text-neon-blue border border-neon-blue/20 rounded-lg text-sm font-medium hover:bg-neon-blue/20 transition-all mb-4">
                        {wmVisible ? <><EyeOff size={14} className="inline mr-2" /> Ocultar</> : <><Eye size={14} className="inline mr-2" /> Revelar</>}
                    </button>
                    <div className="relative bg-black/30 rounded-lg p-8 border border-white/5 min-h-[200px] flex items-center justify-center">
                        <p className="text-gray-400 text-sm">Contenido simulado de la aplicación</p>
                        {wmVisible && (
                            <div className="absolute inset-0 flex items-center justify-center">
                                <span className="text-red-500/30 text-4xl font-bold font-mono rotate-[-30deg] select-none">LIC-AUT360-2026-XXXX</span>
                            </div>
                        )}
                        <div className={`absolute bottom-2 right-2 text-[8px] font-mono transition-all ${wmVisible ? 'text-red-400' : 'text-transparent'}`}>wm:a3b8f2c1</div>
                    </div>
                </Card>
            )}

            {tab === 'config' && (
                <Card>
                    <h2 className="text-white font-bold text-sm mb-4">Configuración</h2>
                    <div className="space-y-3">
                        {[
                            { key: 'wmEnabled', label: 'Watermark Activo', value: 'true' },
                            { key: 'wmLayers', label: 'Capas Activas', value: 'DOM, CSS, Console, Meta' },
                            { key: 'wmPayload', label: 'Payload', value: 'license_id + domain + timestamp' },
                            { key: 'wmRefresh', label: 'Refresh Interval', value: '3600s (1h)' },
                        ].map(c => (
                            <div key={c.key} className="flex items-center justify-between p-3 bg-black/20 rounded-lg">
                                <span className="text-white text-sm">{c.label}</span>
                                <code className="text-neon-blue text-xs font-mono bg-neon-blue/5 px-2 py-1 rounded">{c.value}</code>
                            </div>
                        ))}
                    </div>
                </Card>
            )}

            {tab === 'docs' && (
                <Card>
                    <h2 className="text-white font-bold text-sm mb-3">¿Cómo funciona?</h2>
                    <div className="text-gray-400 text-sm space-y-2 leading-relaxed">
                        <p>Cada instancia desplegada contiene <strong className="text-white">marcadores únicos</strong> invisibles al usuario pero rastreables si se distribuye sin autorización.</p>
                        <p>Los watermarks se inyectan en <strong className="text-white">múltiples capas</strong> (HTML, CSS, JS, Canvas) para que eliminar uno no elimine todos.</p>
                        <p>Si detectamos una copia no autorizada, podemos identificar la licencia original que la generó.</p>
                    </div>
                </Card>
            )}
        </div>
    )
}
